// ═══════════════════════════════════════════════════════════════════════════
// MATCHPROP → `properties/apply` resolution (SHARED one-truth)
// ═══════════════════════════════════════════════════════════════════════════
//
// Extracted from `storeHelpers.ts` (file-size policy) — a small, single-purpose
// pure module. Depends only on the CAD-core packages (command-engine +
// drawing-model + contracts); it does NOT import back into `state/`, so the
// dependency graph stays acyclic. `storeHelpers` re-exports it, so existing
// import sites keep working.

import {
  getPropertiesForChannel,
  resolveMatchpropChannels,
} from "@dxf-ai-editor/command-engine";
import {
  coerceColor,
  colorToLegacyString,
  serializeColor,
  type DrawingDocument,
  type DrawingEntity,
  type EntityColor,
} from "@dxf-ai-editor/drawing-model";
import type {
  EditorCommand,
  EditorShellState,
  EntityPreview,
} from "@dxf-ai-editor/contracts";

/**
 * Injected seams the store wiring (`storeHelpers.resolveGeometryDocumentCommand`)
 * supplies so this pure module stays free of `state/` imports (no dxfGeometry /
 * propertyTranslator back-edge — dependency graph stays acyclic).
 *
 * `resolveSourceEntity` (@codex P2-C #420) — resolves the armed MATCHPROP source
 * from shell-or-DOCUMENT. On large imports `state.drawing.entities` is emptied
 * above `SHELL_ENTITY_PREVIEW_THRESHOLD`, so a shell-only lookup would miss the
 * source and the whole command would no-op; the store passes a resolver that
 * falls back to the document-backed entity (one-truth with how the rest of the
 * resolution reads the document). It returns BOTH the `EntityPreview` (channel
 * property strings) and the typed `DrawingEntity` (lossless colour union).
 *
 * `isLabelDocumentPersistable` (@codex P2-A #420) — keeps only labels the
 * geometry-document translator can actually persist for the source type, so a
 * deferred display-only label (viewport special: `Display lock` / `Scale` / …)
 * is never advertised in the resolved `properties/apply`.
 *
 * `fromShell` (@codex P2-B #420 round-3) — discriminates whether the resolved
 * `preview` came from the reactive shell (`state.drawing.entities`, the VISIBLE
 * value incl. un-persisted shell-only edits) or was synthesised from the
 * document. Source-property reads (esp. the lossless `Color`) MUST prefer the
 * shell preview when `fromShell` is `true`; the document is read ONLY when the
 * shell lacks the source (`fromShell: false`, large-import case). Structurally
 * matches `matchpropStoreDeps.CoherentEntityResolution` (no import back-edge —
 * dependency graph stays acyclic).
 */
export interface MatchpropResolutionDeps {
  resolveSourceEntity?: (entityId: string) => {
    preview: EntityPreview;
    documentEntity?: DrawingEntity;
    fromShell: boolean;
  } | null;
  isLabelDocumentPersistable?: (entityType: string, label: string) => boolean;
}

/**
 * MATCHPROP → `properties/apply` resolution (SHARED one-truth).
 *
 * @codex P1 (#420) — a typed `matchprop/apply` (dispatched DIRECTLY by the AI
 * safe contour AND the Inspector toolbar Match-Properties buttons, NOT as
 * command-line text) previously reached ONLY the shell property reducer: it
 * painted the `EntityPreview` previews + audit but never mutated the geometry
 * DOCUMENT, so undo history, the agent's `affectedEntityIds`, and any
 * export / reload / full-resync LOST the copied properties. The CLI `MATCHPROP
 * [BASIC|SPECIAL|ALL]` text arm did NOT have this bug — it bakes the source's
 * channel properties into a `properties/apply` (which IS routed to the document
 * by `resolveGeometryDocumentCommand`'s allowlist). This helper is that exact
 * resolution, LIFTED so BOTH the CLI text arm and the typed `matchprop/apply`
 * router converge on it — one truth, no duplicated channel-resolution logic, no
 * divergent AI vs human path.
 *
 * Resolves the copied property set with the SAME `resolveMatchpropChannels(mode)`
 * + `getPropertiesForChannel` the shell reducer's `matchProperties` uses, filters
 * the source id out of the targets exactly as the reducer does, reads `Color`
 * LOSSLESSLY from the document union (preserving 62/430 siblings + bookColor),
 * and returns the `properties/apply` command — or `null` when there is no armed
 * source / no eligible target / nothing to copy (caller falls through / no-ops).
 */
export function resolveMatchpropApplyToPropertiesApply(
  state: EditorShellState,
  source: EditorCommand["source"],
  mode: "basic" | "special" | "all",
  rawTargetEntityIds: readonly string[],
  geometryDocument?: DrawingDocument,
  deps?: MatchpropResolutionDeps,
): Extract<EditorCommand, { type: "properties/apply" }> | null {
  const sourceEntityId = state.propertyWorkflows.matchpropSourceEntityId;
  if (!sourceEntityId) return null;
  const targetEntityIds = rawTargetEntityIds.filter(
    (entityId) => entityId !== sourceEntityId,
  );
  if (targetEntityIds.length === 0) return null;

  // @codex P2-C (#420) — resolve the source from shell-or-document. Isolated
  // callers (no `deps`) keep the legacy shell-only lookup; the store passes a
  // document-backed resolver so this works above the shell-preview threshold.
  const resolveSourceEntity: NonNullable<
    MatchpropResolutionDeps["resolveSourceEntity"]
  > =
    deps?.resolveSourceEntity ??
    ((entityId: string) => {
      const hit = state.drawing.entities.find((e) => e.id === entityId);
      // Isolated callers (no `deps`) only ever see the shell → `fromShell: true`.
      return hit ? { preview: hit, fromShell: true } : null;
    });
  const resolvedSource = resolveSourceEntity(sourceEntityId);
  if (!resolvedSource) return null;
  const sourceEntity = resolvedSource.preview;
  const sourceDocumentEntity = resolvedSource.documentEntity;

  // @codex P2-A (#420) — only copy labels the geometry-document translator can
  // actually persist for the source type; deferred display-only labels
  // (viewport special: `Display lock` / `Scale` / …, `Annotative`, `Plot Style`)
  // are dropped so the resolved `properties/apply` never advertises a
  // persistence that no-ops. Isolated callers (no `deps`) keep every label.
  const isLabelPersistable =
    deps?.isLabelDocumentPersistable ?? (() => true);

  const channelPropertyNames = new Set<string>();
  for (const channel of resolveMatchpropChannels(mode)) {
    for (const propertyName of getPropertiesForChannel(
      sourceEntity.type,
      channel,
    )) {
      if (
        propertyName in sourceEntity.properties &&
        isLabelPersistable(sourceEntity.type, propertyName)
      ) {
        channelPropertyNames.add(propertyName);
      }
    }
  }

  // Locate the source's TYPED colour union (documentEntity preferred — cache-
  // aware, works above the shell threshold; `geometryDocument` space scan is the
  // isolated-caller fallback). `undefined` = no document / source not yet in the
  // inline document → the preview `Color` read below wins.
  const sourceDocTypedColor: EntityColor | undefined =
    sourceDocumentEntity !== undefined
      ? coerceColor((sourceDocumentEntity as { color?: unknown }).color)
      : geometryDocument === undefined
        ? undefined
        : (() => {
            for (const space of geometryDocument.spaces) {
              for (const docEntity of space.entities) {
                if (docEntity.id === sourceEntityId) {
                  return coerceColor(
                    (docEntity as { color?: unknown }).color,
                  );
                }
              }
            }
            return undefined;
          })();

  // @codex P2-B (#420 round-3) — source-property precedence: the VISIBLE value
  // always wins. The document colour is used ONLY as the LOSSLESS ENCODING of a
  // value the source already shows:
  //   • `fromShell: false` (shell LACKS the source — large / streamed import) →
  //     the document IS the source of truth → always read it losslessly
  //     (preserving 62/430 siblings + bookColor).
  //   • `fromShell: true` (source in `state.drawing.entities`) → the shell
  //     preview is the visible truth. Use the lossless document colour ONLY when
  //     it PROJECTS to the same legacy string the preview shows
  //     (`colorToLegacyString` is exactly the projection `dxfGeometry` builds the
  //     preview `Color` from) — i.e. shell and document are IN SYNC, so the
  //     document merely recovers the rich siblings of that same visible colour.
  //     When they DIVERGE (an un-persisted shell-only edit — e.g. a
  //     `chprop/apply` that never reached the document, leaving it STALE), drop
  //     the document colour and let the loop below copy the shell preview's
  //     visible `Color` string. This keeps the round-1 lossless rich-colour copy
  //     for the in-sync case AND the visible value for the diverged case.
  const sourceDocColor =
    sourceDocTypedColor === undefined
      ? undefined
      : !resolvedSource.fromShell ||
          colorToLegacyString(sourceDocTypedColor) ===
            sourceEntity.properties["Color"]
        ? serializeColor(sourceDocTypedColor)
        : undefined;

  const values: Record<string, string> = {};
  for (const propertyName of channelPropertyNames) {
    // Copy `Color` LOSSLESSLY from the source document union; every other
    // property keeps the preview string (byte-identical for the common cases).
    if (propertyName === "Color" && sourceDocColor !== undefined) {
      values[propertyName] = sourceDocColor;
      continue;
    }
    const sourceValue = sourceEntity.properties[propertyName];
    if (typeof sourceValue === "string") {
      values[propertyName] = sourceValue;
    }
  }
  if (Object.keys(values).length === 0) return null;
  return {
    type: "properties/apply",
    source,
    entityIds: targetEntityIds,
    values,
  };
}
